<template>
  <div class="column q-pa-md ">
    <div class="col-1 row q-gutter-md items-center" v-if="!isFetching">
      <avatar :project="project" size="36px"/>
      <div class="column">
        <h5 class="q-ma-xs">{{ task?.name }}</h5>
        <div class="q-ma-xs text-subtitle2">{{ task?.code }}</div>
      </div>
      <div class="row q-gutter-xs items-center">
        <q-btn icon="share" round color="primary">
          <q-popup-proxy @beforeShow="generate">
            <q-banner inline-actions>
              <q-input :model-value="linkUrl" readonly :loading="generating"/>
              <template v-slot:action>
                <q-btn @click="copy(linkUrl)" flat :icon="copied ? 'done' : 'content_copy'"
                       color="primary"/>
              </template>
            </q-banner>
          </q-popup-proxy>
        </q-btn>
        <q-btn icon="download" round color="positive" :loading="isLoading" @click="downloadPhotos()">
          <q-tooltip>Скачать</q-tooltip>
        </q-btn>
        <template v-if="task?.canEdit">
          <q-btn icon="edit" round color="primary" :to="`${taskId}/edit`">
            <q-tooltip>Редактировать</q-tooltip>
          </q-btn>
          <q-btn icon="content_copy" round color="primary" :to="`${taskId}/copy`">
            <q-tooltip>Копировать</q-tooltip>
          </q-btn>
          <q-btn v-if="task.status === 'OPEN'" icon="close" round color="negative" @click="closeTask">
            <q-tooltip>Закрыть</q-tooltip>
          </q-btn>
          <q-btn v-if="task.status === 'CLOSED'" icon="check" round color="positive" @click="openTask">
            <q-tooltip>Открыть</q-tooltip>
          </q-btn>
        </template>
        <photo-duplicates :task-id="taskId" :project-id="projectId"/>
      </div>
    </div>
    <div class="col-1 row items-center" v-else>
      <q-skeleton type="QAvatar"/>
      <div class="column ">
        <q-skeleton class="q-ma-xs" type="text" width="100px"/>
        <q-skeleton class="q-ma-xs" type="text" width="20px"/>
      </div>
    </div>
    <div class="col-10 row ">
      <div class="col-12 col-md-6">
        <q-expansion-item switch-toggle-side expand-separator label="Детали">
          <div class="row  q-ma-md  items-center q-gutter-md">
            <div class="col-4">
              Количество фото:
              <span>{{ task?.photoCount }}</span>
            </div>
            <div class="col-4">
              Приоритет:
              <span>{{ task?.priority }}</span>
            </div>
            <div class="col-4">
              Тип:
              <span>{{ task?.type.name }}</span>
            </div>
            <div class="col-4">
              <span>Статус: </span>
              <q-chip :color="statusColors[task?.status]" text-color="white">{{
                  status[task?.status]
                }}
              </q-chip>
            </div>
            <div class="col-4">
              <span>Состояние: </span>
              <q-chip :color="stateColors[task?.state]" text-color="white">{{
                  states[task?.state]
                }}
              </q-chip>
            </div>
            <div class="col-4" v-if="task?.labels.length > 0">
              <span>Ярлыки: </span>
              <q-chip :style="{ backgroundColor: label.color }" text-color="white"
                      v-for="label in task?.labels">
                {{ label.name }}
              </q-chip>
            </div>
            <div class="col-4">
              Создан:
              <span>{{ dayJs.utc(task?.createdAt).local().fromNow() }}</span>
            </div>
            <div class="col-">
              <span>Обновлен: </span>
              <span>{{ dayJs.utc(task?.updatedAt).local().fromNow() }}</span>
            </div>
          </div>
        </q-expansion-item>
      </div>
      <div class="col-12 col-md-6">
        <q-expansion-item switch-toggle-side expand-separator label="Пользователи">
          <div class="row q-ma-md items-center">
            <div class="col">
              Создатель:
              <span>{{ task?.owner.username }}</span>
            </div>
            <div class="col">
              <span>Исполнители: </span>
              <span>{{ task?.assigned.map(u => `${u.username} (${u.email})`).join(", ") }}</span>
            </div>
          </div>
        </q-expansion-item>
      </div>
      <div class="col-12" v-if="task?.properties.length > 0">
        <q-expansion-item switch-toggle-side expand-separator label="Пользовательские детали">
          <div class="row q-ma-md items-center">
            <div class="col" v-for="property in task?.properties">
              {{ propertiesDescription[property.code].name }}:
              <span v-if="propertiesDescription[property.code].type === 'DATE'">
                                {{ dayJs(property.value).format("L") }}
                            </span>
              <span v-else-if="propertiesDescription[property.code].type === 'DATETIME'">
                                {{ dayJs(property.value).format("L LT") }}
                            </span>
              <span v-else>
                                {{ property.value }}
                            </span>
            </div>
          </div>
        </q-expansion-item>
      </div>
      <div class="col-12">
        <q-expansion-item switch-toggle-side expand-separator label="Описание">
          <div class="q-ma-md pre-formatted">
            {{ task?.description || "Описания нет" }}
          </div>
        </q-expansion-item>
      </div>
      <div class="col-12">
        <q-expansion-item switch-toggle-side expand-separator :disable="task?.photosPreview.length < 1"
                          :caption="task?.photosPreview.length < 1 ? 'Фотографии отсутствуют' : undefined"
                          label="Фотографии">
          <div class="row q-gutter-md q-ma-xs">
            <div class="col-12 col-md-2" v-for="photo in task?.photosPreview.slice(0, 3)">
              <q-card>
                <q-img fit="cover" :ratio="16 / 9" v-ripple
                       :src="photoPreviewUrl(photo.address)"/>
              </q-card>
            </div>
            <div class="col-12 col-md-2" :style="$q.screen.xs ? ({ minHeight: '150px' }) : {}">
              <q-card v-ripple @click="openPhotos = !openPhotos" class="bg-grey-4 fit text-center ">
                <q-icon name="more_horiz" color="white" class="absolute-center" size="60px"/>
              </q-card>
            </div>
          </div>
        </q-expansion-item>
      </div>
      <div class="col-12">
        <q-expansion-item switch-toggle-side expand-separator label="Активность">
          <div class="q-ma-md">
            <q-tabs v-model="tab" dense class="text-grey" active-color="primary" indicator-color="primary"
                    align="justify" narrow-indicator>
              <q-tab name="logs" label="Действия"/>
              <q-tab name="comments" label="Комментарии"/>
            </q-tabs>
            <q-separator/>

            <q-tab-panels v-model="tab" animated>
              <q-tab-panel name="logs">
                <task-logs/>
              </q-tab-panel>
              <q-tab-panel name="comments">
                <task-comments/>
              </q-tab-panel>
            </q-tab-panels>

          </div>
        </q-expansion-item>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {useDayJs} from "../../composables/dayjs";
import {useQuasar} from "quasar";
import {useRoute} from "vue-router";
import {computed, ref} from "vue";
import {useTask, useCloseTask, useOpenTask, useTaskLink, useAllTaskPhotos} from "../../composables/task";
import {useProjectStore} from "../../store/project";
import {storeToRefs} from "pinia";
import {status, statusColors, stateColors, states} from "../../util/state";
import Avatar from "../../components/Avatar.vue";
import {usePhotoStore} from "../../store/photo";
import TaskLogs from "../../components/TaskLogs.vue";
import TaskComments from "../../components/TaskComments.vue";
import {useClipboard, useEventBus} from "@vueuse/core";
import {taskKey} from "../../util/events";
import PhotoDuplicates from "../../components/PhotoDuplicates.vue";
import JSZip from "jszip";
import {saveAs} from "file-saver";
import {useLoading} from "../../util/loading";
import {photoCdnUrl} from "../../util/photoCdn";

const route = useRoute()
const $q = useQuasar()

const tab = ref("logs")
const dayJs = useDayJs()
const projectStore = useProjectStore()

const {project} = storeToRefs(projectStore)

const projectId = computed(() => Number(route.params.projectId))
const taskId = computed(() => Number(route.params.taskId))
const propertiesDescription = computed(() => Object.fromEntries(
    task.value?.type.propertyDescription.map(e => [e.code, e]) ?? []
))

const {data: task, isFetching, execute} = useTask(projectId, taskId)
const {execute: executeClose} = useCloseTask(projectId, taskId)
const {execute: executeOpen} = useOpenTask(projectId, taskId)

const taskEvent = useEventBus(taskKey)

const photoStore = usePhotoStore()
const {open: openPhotos} = storeToRefs(photoStore)

const {data: link, isFetching: generating, execute: generate} = useTaskLink(projectId, taskId)

const linkUrl = computed(() => `${window.location.origin}/task?share=${link.value?.token}`)

const {text, copy, copied, isSupported} = useClipboard({source: linkUrl})

const photoPreviewUrl = (address: string) => photoCdnUrl(address, {
  preview: '1280x960',
  formatAfterCaption: 'jpeg',
})

async function closeTask() {
  await executeClose(true)
  await execute(true)
  taskEvent.emit('refresh')
  $q.dialog({
    title: "Задача",
    message: "Задача была закрыта",
    cancel: false
  })
}

const { isLoading, startLoading, stopLoading, withLoading } = useLoading();

const downloadPhotos = withLoading(async () => {
  const zip = new JSZip();
  const {data} = await useAllTaskPhotos(projectId, taskId);
  const photos = data.value?.content;
  const photosUrl = photos?.map(p => photoCdnUrl(p.address, {
    preview: '960x960',
    formatBeforeCaption: 'jpeg',
    caption: p.caption ? {text: p.caption, fontSize: 30} : null,
  }))

  if (photosUrl) {
    for (let i = 0; i < photosUrl?.length; i++) {
      const url = photosUrl[i];
      const res = await fetch(url);
      const blob = await res.blob();
      zip.file(`${i}.jpeg`, blob);
    }
  }

  const content = await zip.generateAsync({type: "blob"});

  saveAs(content, `Фото-${task.value?.name}.zip`);
});

async function openTask() {
  await executeOpen(true)
  await execute(true)
  taskEvent.emit('refresh')
  $q.dialog({
    title: "Задача",
    message: "Задача была открыта",
    cancel: false
  })
}
</script>

<style scoped>
.pre-formatted {
  white-space: pre;
}
</style>
